import "./ScheduleCarousel.css";

import dayjs from "dayjs";
import { memo, SyntheticEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";

import { ISoccerMatchListItem, SoccerMatchStatus } from "../../../../types";
import { appTournamentConfig, TOURNAMENT_ID } from "../../config/tournament";
import { getTeamInfo } from "../../hooks/useTeamLogos";
import { useScheduleMatches } from "../../hooks/useScheduleMatches";

const isClubTournament =
  appTournamentConfig[TOURNAMENT_ID]?.isClubTournament ?? false;
import { useFixtureScore } from "../../stores";
import {
  buildNavigationUrl,
  SbsAnalyticsService,
  openSbsOnDemand,
} from "../../utils";

const handleLogoError = (e: SyntheticEvent<HTMLImageElement>) => {
  e.currentTarget.src = "/images/teamLogos/flag-fallback.png";
};

const ScheduleCard = memo(function ScheduleCard({
  match,
  liveMinute,
  onSelect,
}: {
  match: ISoccerMatchListItem;
  liveMinute?: string;
  onSelect: (match: ISoccerMatchListItem) => void;
}) {
  const isLive = match.status === SoccerMatchStatus.PLAYING;
  const isFinished = match.status === SoccerMatchStatus.PLAYED;
  const isUpcoming = !isLive && !isFinished;

  const home = match.contestants?.[0];
  const away = match.contestants?.[1];
  const homeInfo = getTeamInfo(home?.name ?? "");
  const awayInfo = getTeamInfo(away?.name ?? "");

  // Show FT score (or ET if available), fallback to total
  const scoreSource =
    match.scores?.et ?? match.scores?.ft ?? match.scores?.total;
  const homeScore = scoreSource?.home ?? 0;
  const awayScore = scoreSource?.away ?? 0;
  const homePen = match.scores?.pen?.home;
  const awayPen = match.scores?.pen?.away;
  const hasPenalties = homePen != null && awayPen != null;

  const ctaClass = isLive ? "live" : isFinished ? "finished" : "upcoming";
  const centerClass = `schedule-center ${ctaClass}`;
  const ctaText = isLive
    ? "Stream LIVE on SBS"
    : isFinished
      ? "Stream replay on SBS"
      : "SBS On Demand / SBS";

  const stageDescription = match.stage?.name ? `${match.stage.name} Match` : "";

  return (
    <div
      className={`schedule-card ${isLive ? "live" : ""}`}
      role="button"
      tabIndex={0}
      onClick={() => onSelect(match)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(match);
        }
      }}
    >
      <div className="schedule-card-body">
        {isLive && (
          <div className="schedule-live-badge">
            <span className="schedule-live-badge-dot" />
            <span className="schedule-live-badge-text">Live</span>
          </div>
        )}

        <div className="schedule-match-data">
          <div className="schedule-match-teams">
            <div className="schedule-team">
              <img
                className={`schedule-team-flag ${isClubTournament ? "club-flag" : ""}`}
                src={homeInfo.imgSrc}
                alt={home?.name ?? ""}
                onError={handleLogoError}
              />
              <span className="schedule-team-name">
                {home?.code || home?.shortName || home?.name || "TBD"}
              </span>
            </div>

            <div className={centerClass}>
              {isLive && (
                <>
                  {liveMinute === "HT" ? (
                    <span className="schedule-status-label">Half time</span>
                  ) : (
                    <div className="schedule-live-time">
                      <span className="schedule-live-time-dot" />
                      <span className="schedule-live-time-text">
                        {liveMinute || "LIVE"}
                      </span>
                    </div>
                  )}
                  <div className="schedule-scores">
                    <span>
                      {homeScore}
                      {hasPenalties ? `(${homePen})` : ""}
                    </span>
                    <span>
                      {awayScore}
                      {hasPenalties ? `(${awayPen})` : ""}
                    </span>
                  </div>
                </>
              )}
              {isFinished && (
                <>
                  <span className="schedule-status-label">Full-time</span>
                  <div className="schedule-scores">
                    <span>
                      {homeScore}
                      {hasPenalties ? `(${homePen})` : ""}
                    </span>
                    <span>
                      {awayScore}
                      {hasPenalties ? `(${awayPen})` : ""}
                    </span>
                  </div>
                </>
              )}
              {isUpcoming && (
                <>
                  <span className="schedule-status-label">
                    {dayjs(match.matchDateTime).format("ddd, MMM D")}
                  </span>
                  <span className="schedule-upcoming-time">
                    {match.matchDateTime.split("T")[1]
                      ? dayjs(match.matchDateTime).format("h:mma")
                      : "TBD"}
                  </span>
                </>
              )}
            </div>

            <div className="schedule-team">
              <img
                className={`schedule-team-flag ${isClubTournament ? "club-flag" : ""}`}
                src={awayInfo.imgSrc}
                alt={away?.name ?? ""}
                onError={handleLogoError}
              />
              <span className="schedule-team-name">
                {away?.code || away?.shortName || away?.name || "TBD"}
              </span>
            </div>
          </div>

          <span className="schedule-group-label">{stageDescription}</span>
        </div>
      </div>

      <button
        className={`schedule-cta ${ctaClass}`}
        onClick={(e) => {
          e.stopPropagation();
          const event = isLive
            ? "stream_live_on_demand"
            : isFinished
              ? "stream_replay_on_SBS"
              : "stream_now_on_demand";
          SbsAnalyticsService.trackEvent(event, {
            match_id: match.id,
            tournament_id: TOURNAMENT_ID,
          });
          openSbsOnDemand({
            placement: "horizontalschedule",
            state: isFinished ? "post" : isUpcoming ? "pre" : "live",
          });
        }}
      >
        {isLive && (
          <>
            <img
              className="schedule-cta-bg-mask"
              src="/images/schedule-cta/mask-group.svg"
              alt=""
            />
            <img
              className="schedule-cta-bg-subtract"
              src="/images/schedule-cta/subtract.svg"
              alt=""
            />
          </>
        )}
        <div className="schedule-cta-content">
          <div className="schedule-cta-label">
            <span className="schedule-cta-text">{ctaText}</span>
          </div>
          {(isLive || isFinished) && (
            <div className="schedule-cta-icon">
              <div className="schedule-cta-icon-box">
                <img
                  src="/images/schedule-cta/arrow-icon.svg"
                  alt=""
                  className="schedule-cta-arrow-img"
                />
              </div>
            </div>
          )}
        </div>
      </button>
    </div>
  );
});

function StreamFreeCta() {
  return (
    <button
      className="schedule-stream-cta"
      onClick={() => {
        SbsAnalyticsService.trackEvent("stream_free_on_sbs", {
          tournament_id: TOURNAMENT_ID,
        });
        openSbsOnDemand({ placement: "horizontalschedule", state: "blackbox" });
      }}
    >
      <span className="schedule-stream-cta-text">Stream free on SBS</span>
      <span className="schedule-stream-cta-arrow">
        <img
          src="/images/schedule-cta/arrow-icon.svg"
          alt=""
          className="schedule-stream-arrow-img"
        />
      </span>
    </button>
  );
}

export function ScheduleCarousel({
  onSelect,
}: {
  onSelect?: (match: ISoccerMatchListItem) => void;
} = {}) {
  const { matches, liveMinutes, isLoading } = useScheduleMatches();
  const navigate = useNavigate();
  const setMatch = useFixtureScore(useShallow(({ setMatch }) => setMatch));

  if (isLoading || matches.length === 0) return null;

  const handleSelect = (match: ISoccerMatchListItem) => {
    if (onSelect) {
      onSelect(match);
      return;
    }
    setMatch(match);
    navigate(buildNavigationUrl("/match-centre", { matchId: match.id }));
  };

  return (
    <div className="schedule-carousel">
      {matches.map((match) => (
        <ScheduleCard
          key={match.id}
          match={match}
          liveMinute={liveMinutes[match.id]}
          onSelect={handleSelect}
        />
      ))}
      <StreamFreeCta />
    </div>
  );
}
